import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ShieldCheck, Loader2, Wallet as WalletIcon,
         Trophy, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

/**
 * Elite Wallet Induction · public landing page.
 *
 * Renders a monumental "You've been inducted" screen with a 2-second
 * System Sync animation before consuming the one-time token and
 * routing the user to their live wallet.
 */
export default function WalletActivate() {
  const { token } = useParams();
  const { user, api } = useAuth();
  const nav = useNavigate();
  const [phase, setPhase] = useState("loading"); // loading | ready | activating | activated | expired | wrong_user | not_found
  const [info, setInfo] = useState(null);
  const [syncPct, setSyncPct] = useState(0);

  const lookup = useCallback(async () => {
    try {
      const { data } = await api.get(`/wallet/activate/${token}`);
      setInfo(data);
      if (data.status === "already_activated") setPhase("activated");
      else if (data.status === "expired")      setPhase("expired");
      else                                       setPhase("ready");
    } catch (e) {
      setPhase("not_found");
    }
  }, [api, token]);

  useEffect(() => { lookup(); }, [lookup]);

  const activate = async () => {
    if (!user || !user.email) {
      toast.info("Please sign in first to activate your wallet.");
      nav(`/login?next=/wallet/activate/${token}`);
      return;
    }
    setPhase("activating"); setSyncPct(0);
    // Kick off the "System Sync" progress in parallel with the API call
    const started = Date.now();
    const tick = () => {
      const pct = Math.min(100, ((Date.now() - started) / 2000) * 100);
      setSyncPct(pct);
      if (pct < 100) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    try {
      await api.post(`/wallet/activate/${token}`);
      // Wait for the full 2s animation before flipping to Success
      const remaining = Math.max(0, 2100 - (Date.now() - started));
      setTimeout(() => {
        setPhase("activated");
        setTimeout(() => nav("/wallet"), 1400);
      }, remaining);
    } catch (e) {
      const detail = e.response?.data?.detail || "Activation failed";
      if (e.response?.status === 403) { setPhase("wrong_user"); }
      else if (e.response?.status === 410) { setPhase("expired"); }
      else {
        toast.error(detail);
        setPhase("ready");
      }
    }
  };

  return (
    <div data-testid="wallet-activate" className="relative min-h-screen bg-[#050505] text-[#F5F5F0] px-6 py-16 overflow-hidden">
      {/* Cinematic aura */}
      <div className="pointer-events-none absolute inset-0"
           style={{
             background: "radial-gradient(1000px 700px at 50% 30%, rgba(216,178,62,0.22), transparent 60%),"
                        + "radial-gradient(800px 500px at 50% 90%, rgba(184,115,51,0.14), transparent 60%)",
           }} />
      {/* Grain */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.7'/></svg>\")",
            }} />

      <div className="relative max-w-2xl mx-auto">
        <AnimatePresence mode="wait">
          {phase === "loading" && (
            <motion.div key="l" className="min-h-[60vh] grid place-items-center text-[#8A8A82]"
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Loader2 className="animate-spin" />
            </motion.div>
          )}

          {phase === "ready" && (
            <motion.div key="r" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                          className="text-center space-y-8 py-16">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.42em] text-[#8A6A1A]">
                Vinci · Certified Induction
              </div>
              <motion.div className="mx-auto w-28 h-28 rounded-full grid place-items-center"
                             animate={{ boxShadow: ["0 0 60px rgba(216,178,62,0.4)",
                                                     "0 0 100px rgba(216,178,62,0.7)",
                                                     "0 0 60px rgba(216,178,62,0.4)"] }}
                             transition={{ duration: 3, repeat: Infinity }}
                             style={{ background: "linear-gradient(135deg,#D8B23E,#F6E4A6,#B87333)" }}>
                <Trophy size={48} className="text-[#0A0906]" />
              </motion.div>
              <div>
                <div className="font-serif text-4xl md:text-6xl leading-tight tracking-tight"
                      style={{ textShadow: "0 0 80px rgba(216,178,62,0.4)" }}>
                  You have been granted<br/>
                  access to the{" "}
                  <span className="italic"
                         style={{ background: "linear-gradient(90deg,#D8B23E,#F6E4A6,#B87333)",
                                   WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                    Vinci Executive Wallet.
                  </span>
                </div>
              </div>
              <p className="text-[#C9C9C2] font-body text-[15px] max-w-xl mx-auto leading-relaxed">
                Namaste {(info?.name || info?.user_email?.split("@")[0] || "Trader").toString()}, — you're joining a
                curated cohort of counterparties trading at machine speed
                with zero per-transaction friction. Once activated, your
                wallet is live for recharges, instant settlement, and
                closed-loop trade.
              </p>
              <Button data-testid="wallet-activate-confirm" onClick={activate}
                       className="px-10 py-6 text-[11px] font-grotesk uppercase tracking-[0.32em] rounded-sm text-black bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] hover:brightness-110 shadow-[0_20px_80px_-10px_rgba(216,178,62,0.5)]">
                <Sparkles size={13} className="mr-2" /> Activate My Wallet
              </Button>
              <div className="text-[10px] text-[#6f6f68] font-body">
                One-time · Non-transferable · Signed in as{" "}
                <b className="text-[#8A8A82]">{user?.email || "guest"}</b>
              </div>
            </motion.div>
          )}

          {phase === "activating" && (
            <motion.div key="a" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                          className="text-center space-y-10 py-24">
              <div className="font-grotesk text-[10px] uppercase tracking-[0.42em] text-[#D8B23E]">
                System Sync in progress
              </div>
              <div className="mx-auto w-24 h-24 rounded-full border-4 border-[#D8B23E]/30 grid place-items-center relative">
                <motion.div className="absolute inset-0 rounded-full border-4 border-[#D8B23E]"
                              style={{ borderTopColor: "transparent", borderRightColor: "transparent" }}
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }} />
                <WalletIcon size={38} className="text-[#D8B23E]" />
              </div>
              <div className="max-w-md mx-auto space-y-3">
                <div className="h-[3px] bg-white/10 overflow-hidden rounded-full">
                  <motion.div className="h-full bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333]"
                                animate={{ width: `${syncPct}%` }}
                                transition={{ duration: 0.1 }} />
                </div>
                <div className="font-mono text-[11px] text-[#8A8A82]">
                  Registering signature · Binding to ledger · Provisioning wallet · {Math.round(syncPct)}%
                </div>
              </div>
            </motion.div>
          )}

          {phase === "activated" && (
            <motion.div key="s" initial={{ opacity: 0, scale: 0.94 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.6 }}
                          className="text-center space-y-8 py-20">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                             transition={{ delay: 0.1, type: "spring", stiffness: 220, damping: 14 }}
                             className="mx-auto w-32 h-32 rounded-full grid place-items-center"
                             style={{ background: "linear-gradient(135deg,#D8B23E,#F6E4A6,#B87333)",
                                       boxShadow: "0 0 120px rgba(216,178,62,0.7)" }}>
                <CheckCircle2 size={64} className="text-[#0A0906]" />
              </motion.div>
              <div className="font-serif text-5xl md:text-6xl italic"
                    style={{ background: "linear-gradient(90deg,#D8B23E,#F6E4A6,#B87333)",
                              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Wallet Activated.
              </div>
              <p className="text-[#C9C9C2] font-body">
                Redirecting to your Vinci Executive Wallet…
              </p>
            </motion.div>
          )}

          {phase === "expired" && (
            <motion.div key="e" className="text-center py-24 space-y-4"
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="font-serif text-4xl text-[#C0392B]">Invite Expired</div>
              <p className="text-[#8A8A82] font-body">
                This induction link has passed its 48-hour validity. Please
                ask the admin to regenerate a fresh Wallet Invitation from the
                admin console.
              </p>
              <Link to="/account" className="inline-block mt-4 text-[#B87333] hover:text-[#D8B23E] font-grotesk text-[10px] uppercase tracking-[0.22em]">
                ← Return to Account
              </Link>
            </motion.div>
          )}

          {phase === "wrong_user" && (
            <motion.div key="w" className="text-center py-24 space-y-4"
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="font-serif text-4xl text-[#C0392B]">Wrong Account</div>
              <p className="text-[#8A8A82] font-body">
                This invite belongs to <b>{info?.user_email}</b>. Sign in
                as that user to complete activation.
              </p>
            </motion.div>
          )}

          {phase === "not_found" && (
            <motion.div key="n" className="text-center py-24 space-y-4"
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="font-serif text-4xl text-[#8A8A82]">Invite Not Found</div>
              <p className="text-[#6f6f68] font-body">
                This activation link is invalid or has been consumed.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-12 pt-6 border-t border-white/10 text-[10px] text-[#6f6f68] font-body italic inline-flex items-start gap-2">
          <ShieldCheck size={12} className="text-[#8A6A1A] mt-0.5 shrink-0" />
          Vinci Wallet is a closed-loop digital ledger usable only within
          the Vinci Trade Asia ecosystem. Funds are held in the Vinci
          Business Escrow Account. Not a bank deposit. Not covered by DICGC.
        </div>
      </div>
    </div>
  );
}
